import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserCheck, FileText, Activity } from 'lucide-react';
import { AdminMetrics, getAdminMetrics } from '../services/adminMetricsService';

const formatNumber = (value: number) => new Intl.NumberFormat('es-ES').format(value);

// Stat Card Component - Stripe style (white bg + border)
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  sublabel?: string;
  iconBg: string;
  iconColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, sublabel, iconBg, iconColor }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs lg:text-sm font-medium text-slate-500 uppercase tracking-wide truncate">
          {label}
        </p>
        <p className="text-xl lg:text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {sublabel && (
          <p className="text-xs text-slate-500 mt-1 truncate">{sublabel}</p>
        )}
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg} ${iconColor} flex-shrink-0`}>
        {icon}
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { t } = useTranslation(['app', 'common']);
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      setError(null);
      const data = await getAdminMetrics();
      if (!data) {
        setError(t('app:admin.errorLoadingMetrics'));
      }
      setMetrics(data);
      setLoading(false);
    };

    loadMetrics();
  }, [t]);

  const stats: StatCardProps[] = metrics
    ? [
        {
          label: t('app:admin.activeClinicians'),
          value: formatNumber(metrics.totalClinicians),
          icon: <Users className="w-5 h-5" />,
          sublabel: t('app:admin.activeCount', { value: formatNumber(metrics.activeClinicians) }),
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
        },
        {
          label: t('app:admin.patients'),
          value: formatNumber(metrics.totalPatients),
          icon: <UserCheck className="w-5 h-5" />,
          sublabel: t('app:admin.activeRecords'),
          iconBg: 'bg-emerald-100',
          iconColor: 'text-emerald-600',
        },
        {
          label: t('app:admin.tests'),
          value: formatNumber(metrics.totalTests),
          icon: <FileText className="w-5 h-5" />,
          sublabel: t('app:admin.thisMonth', { value: formatNumber(metrics.testsThisMonth) }),
          iconBg: 'bg-indigo-100',
          iconColor: 'text-indigo-600',
        },
        {
          label: t('app:admin.evolutivos'),
          value: formatNumber(metrics.totalEvolutivos),
          icon: <Activity className="w-5 h-5" />,
          sublabel: t('app:admin.thisMonth', { value: formatNumber(metrics.evolutivosThisMonth) }),
          iconBg: 'bg-amber-100',
          iconColor: 'text-amber-600',
        },
      ]
    : [];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 lg:p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700 mb-2">
            {t('app:admin.panelBadge')}
          </div>
          <h2 className="text-lg lg:text-xl font-bold text-slate-900">
            {t('app:admin.teamTracking')}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t('app:admin.teamTrackingDesc')}
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-xs text-slate-500">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          {t('app:admin.realTimeUpdated')}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500 text-sm">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          {t('app:admin.loadingMetrics')}
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Content */}
      {!loading && metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {stats.map(({ label, value, icon, sublabel, iconBg, iconColor }) => (
            <StatCard
              key={label}
              label={label}
              value={value}
              icon={icon}
              sublabel={sublabel}
              iconBg={iconBg}
              iconColor={iconColor}
            />
          ))}
        </div>
      )}
    </div>
  );
}
